import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────
const BROKER_WHATSAPP = '917770060901'; // for enquiry form prefill

const PROCESS_STEPS = [
  {
    num: '01',
    title: 'The Brief',
    body: 'A private conversation to understand your intent, not just your budget. We map your capital allocation goals, timeline, and risk profile before touching a single listing.',
  },
  {
    num: '02',
    title: 'Market Intelligence',
    body: 'Forensic scan of relevant micro-markets. Off-market inventory, yield data, title risk flags, builder track records. The picture most agents never show you.',
  },
  {
    num: '03',
    title: 'Curated Shortlist',
    body: 'Three to five properties. Never more. Each stress-tested against your brief. We do not flood your inbox. We bring you decisions.',
  },
  {
    num: '04',
    title: 'Negotiation & Due Diligence',
    body: 'Title verification, encumbrance checks, RERA compliance, and legal clarity before any LOI is signed. We negotiate from your position of strength.',
  },
  {
    num: '05',
    title: 'Close & Beyond',
    body: 'Registration, handover, and a standing relationship. We remain your point of contact for resale, yield optimisation, and future acquisitions.',
  },
];

const IMAGES = {
  process: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?q=85&w=2560&auto=format&fit=crop',
};

const EASE = [0.22, 1, 0.36, 1];

function useMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn, { passive: true });
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

// ─── Global Styles ────────────────────────────────────────────────────────────
export const G = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body {
    background: #060606;
    color: #FFFFFF;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 300;
    line-height: 1.85;
    overflow-x: hidden;
  }
  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-track { background: #060606; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); }
  ::selection { background: rgba(255,255,255,0.15); color: #fff; }
  a { color: inherit; text-decoration: none; }
  input, textarea, select, button { font-family: inherit; }
  input::placeholder { color: rgba(255,255,255,0.15); }

  @keyframes grain {
    0%, 100% { transform: translate(0,0); }
    10%  { transform: translate(-2%,-3%); }
    30%  { transform: translate(3%,-1%); }
    50%  { transform: translate(-1%, 2%); }
    70%  { transform: translate(2%, 1%); }
    90%  { transform: translate(-3%, 2%); }
  }
`;

// Grain overlay — animating SVG noise
function Grain() {
  return (
    <div style={{
      position: 'fixed', inset: '-50%',
      width: '200%', height: '200%',
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'repeat',
      backgroundSize: '128px',
      opacity: 0.028,
      pointerEvents: 'none',
      zIndex: 9990,
      animation: 'grain 8s steps(10) infinite',
      mixBlendMode: 'overlay',
    }}/>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────
function Brand({ mobile }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <img
        src="/logo-white.svg" alt="BrokerInASuit"
        style={{ height: mobile ? 18 : 22, aspectRatio: '300 / 38.446', display: 'block' }}
      />
      {!mobile && (
        <span style={{
          fontFamily: 'Helvetica Neue, Helvetica, sans-serif', fontSize: 8,
          fontWeight: 600, letterSpacing: '0.28em', color: 'rgba(255,255,255,0.35)',
          textTransform: 'uppercase', lineHeight: 1, marginTop: 4, whiteSpace: 'nowrap',
          paddingLeft: '0.28em',
        }}>Real Estate Tailored For You</span>
      )}
    </div>
  );
}

// Brand mark starts large in the centre of a dark cover, then glides into
// its slot in the nav. The nav's own mark stays hidden until this lands.
function Intro({ targetRef, onMove, onDone }) {
  const mobile = useMobile();
  const [from, setFrom] = useState(null);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    const r = targetRef.current.getBoundingClientRect();
    const scale = Math.min(mobile ? 2 : 3.2, (window.innerWidth * 0.8) / r.width);
    setFrom({
      left: r.left, top: r.top, scale,
      x: window.innerWidth / 2 - (r.width * scale) / 2 - r.left,
      y: window.innerHeight / 2 - (r.height * scale) / 2 - r.top,
    });
    const t = setTimeout(() => { setMoving(true); onMove(); }, 1500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: moving ? 0 : 1 }}
        transition={{ duration: 0.9, ease: EASE }}
        style={{ position: 'fixed', inset: 0, background: '#060606', zIndex: 150 }}
      />
      {from && (
        <motion.div
          initial={{ x: from.x, y: from.y, scale: from.scale, opacity: 0 }}
          animate={moving
            ? { x: 0, y: 0, scale: 1, opacity: 1 }
            : { x: from.x, y: from.y, scale: from.scale, opacity: 1 }}
          transition={moving ? { duration: 1.1, ease: EASE } : { duration: 0.8, ease: 'easeOut' }}
          onAnimationComplete={() => { if (moving) onDone(); }}
          style={{
            position: 'fixed', left: from.left, top: from.top, zIndex: 250,
            transformOrigin: 'top left', pointerEvents: 'none',
          }}
        >
          <Brand mobile={mobile}/>
        </motion.div>
      )}
    </>
  );
}

function Nav({ brandRef, stage }) {
  const [scrolled, setScrolled] = useState(false);
  const mobile = useMobile();
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: 72,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: mobile ? '0 24px' : '0 56px', zIndex: 200,
      background: scrolled ? 'rgba(6,6,6,0.88)' : 'transparent',
      backdropFilter: scrolled ? 'blur(28px)' : 'none',
      borderBottom: scrolled ? '1px solid rgba(255,255,255,0.05)' : 'none',
      transition: 'background 0.5s ease, backdrop-filter 0.5s ease, border-color 0.5s ease',
    }}>
      <div ref={brandRef} style={{ opacity: stage === 'done' ? 1 : 0 }}>
        <Brand mobile={mobile}/>
      </div>

      {stage !== 'intro' && <motion.div
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3, duration: 0.6, ease: EASE }}
        style={{ display: 'flex', alignItems: 'center', gap: mobile ? 16 : 44 }}
      >
        {!mobile && ['about', 'process'].map(id => (
          <button key={id} onClick={() => go(id)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 10, letterSpacing: '0.34em', textTransform: 'uppercase',
            fontWeight: 600, color: 'rgba(255,255,255,0.45)', transition: 'color 0.2s',
          }}
            onMouseEnter={e => e.target.style.color = '#fff'}
            onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.45)'}
          >{id[0].toUpperCase() + id.slice(1)}</button>
        ))}
        <button onClick={() => go('enquire')} style={{
          background: 'transparent', cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.3)',
          color: '#fff', fontSize: 9, letterSpacing: '0.34em',
          textTransform: 'uppercase', fontWeight: 700,
          padding: mobile ? '10px 18px' : '11px 26px',
          transition: 'background 0.2s, border-color 0.2s',
        }}>Enquire</button>
      </motion.div>}
    </nav>
  );
}

// ─── Hero + Enquiry (combined) ────────────────────────────────────────────────
function Hero() {
  const ref = useRef(null);
  const mobile = useMobile();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY    = useTransform(scrollYProgress, [0, 1], ['0%', '28%']);
  const fadeOut = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const liftOut = useTransform(scrollYProgress, [0, 0.6], ['0%', '-40%']);

  const [form, setForm] = useState({ name: '', email: '', whatsapp: '' });
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState(null);
  const [errors, setErrors] = useState({});

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const validateIndianNumber = (v) => /^[6-9]\d{9}$/.test(v.trim().replace(/\s/g, ''));

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, whatsapp } = form;
    const errs = {};
    if (!name.trim())                    errs.name     = 'Required';
    if (!validateEmail(email))           errs.email    = 'Enter a valid email';
    if (!validateIndianNumber(whatsapp)) errs.whatsapp = 'Enter a valid Indian mobile number';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    const msg = [
      'hey, I have an enquiry', '',
      `*Name:* ${name.trim()}`,
      `*Email:* ${email.trim()}`,
      `*WhatsApp:* +91${whatsapp.trim()}`,
    ].join('\n');
    window.open(`https://wa.me/${BROKER_WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank');
    setSubmitted(true);
  };

  const fieldStyle = (id) => ({
    width: '100%', background: 'transparent', border: 'none',
    borderBottom: `1px solid ${focused === id ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.12)'}`,
    color: '#fff', fontSize: 13, fontWeight: 300,
    padding: '13px 0', outline: 'none',
    transition: 'border-color 0.22s',
    boxShadow: focused === id ? '0 1px 0 0 rgba(255,255,255,0.12)' : 'none',
  });

  return (
    <section ref={ref} id="hero" style={{
      position: 'relative', minHeight: '100vh', overflow: 'hidden',
      display: 'grid',
      gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
    }}>
      {/* Parallax hero image */}
      <motion.div style={{ position: 'absolute', inset: '-28% 0', y: imgY, zIndex: 0 }}>
        <img
          src="https://images.unsplash.com/photo-1739254831441-b81c435c8ea9?q=90&w=3840&auto=format&fit=crop"
          alt=""
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            filter: 'brightness(0.52) contrast(1.08) saturate(0.72)',
          }}
        />
      </motion.div>

      {/* Gradient overlays */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: mobile
          ? 'rgba(6,6,6,0.65)'
          : 'linear-gradient(to right, rgba(6,6,6,0.78) 0%, rgba(6,6,6,0.35) 55%, rgba(6,6,6,0.6) 100%)',
      }}/>
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', zIndex: 1,
        background: 'linear-gradient(to top, #060606, transparent)',
      }}/>

      {/* Animated glow orb */}
      <motion.div
        animate={{ opacity: [0.5, 0.85, 0.5], scale: [1, 1.08, 1] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', right: '18%', top: '30%',
          width: 520, height: 520, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.055) 0%, transparent 68%)',
          filter: 'blur(48px)', pointerEvents: 'none', zIndex: 1,
        }}
      />

      {/* Vertical pillars — desktop only */}
      {!mobile && (
        <div style={{
          position: 'absolute', left: 24, top: '50%',
          transform: 'translateY(-50%)',
          writingMode: 'vertical-rl', display: 'flex', gap: 28,
          color: 'rgba(255,255,255,0.12)', fontSize: 7,
          letterSpacing: '0.44em', textTransform: 'uppercase', fontWeight: 600,
          zIndex: 2,
        }}>
          {['Uncompromised', 'Confidential', 'Curated', 'Independent'].map(t => (
            <span key={t}>{t}</span>
          ))}
        </div>
      )}

      {/* ── Hero copy ── */}
      <motion.div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: mobile ? '110px 28px 48px' : '120px 72px 100px 88px',
        opacity: fadeOut, y: liftOut,
      }}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.65, ease: EASE }}
          style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 44 }}
        >
          <div style={{ width: 28, height: 1, background: 'rgba(255,255,255,0.35)' }}/>
          <span style={{
            fontSize: 9, letterSpacing: '0.46em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)', fontWeight: 700,
          }}>Pune · Real Estate Advisory</span>
        </motion.div>

        <div style={{ overflow: 'hidden', marginBottom: 12 }}>
          <motion.h1
            initial={{ y: '105%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.42, duration: 1.0, ease: EASE }}
            style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: mobile ? 'clamp(52px, 14vw, 80px)' : 'clamp(60px, 7.5vw, 124px)',
              fontWeight: 700, lineHeight: 0.87, letterSpacing: '-0.025em',
              paddingBottom: '0.2em', // keeps the descender on "Always." from being clipped
              textShadow: '0 0 120px rgba(255,255,255,0.08)',
            }}
          >
            You First.<br/>
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.6)' }}>Always.</em>
          </motion.h1>
        </div>

      </motion.div>

      {/* ── Enquiry form ── */}
      <div id="enquire" style={{
        position: 'relative', zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: mobile ? '0 24px 80px' : '120px 72px 100px 40px',
      }}>
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.55, duration: 1.0, ease: EASE }}
          style={{
            width: '100%', maxWidth: 440,
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(13,13,13,0.82)',
            backdropFilter: 'blur(32px)',
            padding: '48px 44px',
            boxShadow: '0 0 80px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.07)',
          }}
        >
          {submitted ? (
            <div style={{ padding: '44px 0' }}>
              <div style={{
                fontFamily: 'Playfair Display, serif', fontSize: 52,
                fontWeight: 700, color: '#fff', marginBottom: 20, lineHeight: 1,
                textShadow: '0 0 60px rgba(255,255,255,0.25)',
              }}>Noted.</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.85 }}>
                Your enquiry has been forwarded. Expect a personal response within 2 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Form header */}
              <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', marginBottom: 44,
              }}>
                <span style={{
                  fontSize: 9, letterSpacing: '0.4em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.3)', fontWeight: 700,
                }}>Private Enquiry</span>
                <div style={{
                  width: 6, height: 6, border: '1px solid rgba(255,255,255,0.2)',
                  transform: 'rotate(45deg)',
                }}/>
              </div>

              {[
                { id: 'name',     label: 'Full Name',       type: 'text',     ph: 'Your full name' },
                { id: 'email',    label: 'Email Address',   type: 'email',    ph: 'your@email.com' },
                { id: 'whatsapp', label: 'WhatsApp Number', type: 'tel',      ph: '98765 43210' },
              ].map(({ id, label, type, ph }) => (
                <div key={id} style={{ marginBottom: 34 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 9 }}>
                    <label style={{
                      fontSize: 8, letterSpacing: '0.36em',
                      textTransform: 'uppercase', fontWeight: 700,
                      color: errors[id] ? 'rgba(255,100,100,0.85)' : focused === id ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.22)',
                      transition: 'color 0.2s',
                    }}>{label}</label>
                    {errors[id] && (
                      <span style={{
                        fontSize: 8, letterSpacing: '0.08em', color: 'rgba(255,100,100,0.75)',
                        fontWeight: 400,
                      }}>{errors[id]}</span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex', alignItems: type === 'textarea' ? 'flex-start' : 'baseline', gap: 8,
                    borderBottom: `1px solid ${errors[id] ? 'rgba(255,100,100,0.6)' : focused === id ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.12)'}`,
                    transition: 'border-color 0.22s',
                  }}>
                    {id === 'whatsapp' && (
                      <span style={{
                        fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.35)',
                        whiteSpace: 'nowrap', paddingBottom: 13,
                      }}>+91</span>
                    )}
                    {type === 'textarea' ? (
                      <textarea
                        placeholder={ph}
                        value={form[id]}
                        rows={3}
                        onChange={e => {
                          setForm(f => ({ ...f, [id]: e.target.value }));
                          if (errors[id]) setErrors(er => ({ ...er, [id]: undefined }));
                        }}
                        onFocus={() => setFocused(id)}
                        onBlur={() => setFocused(null)}
                        autoComplete="off" spellCheck={false}
                        style={{
                          ...fieldStyle(id), borderBottom: 'none', flex: 1,
                          resize: 'none', lineHeight: 1.7,
                        }}
                      />
                    ) : (
                      <input
                        type={type} placeholder={ph}
                        value={form[id]}
                        onChange={e => {
                          setForm(f => ({ ...f, [id]: e.target.value }));
                          if (errors[id]) setErrors(er => ({ ...er, [id]: undefined }));
                        }}
                        onFocus={() => setFocused(id)}
                        onBlur={() => setFocused(null)}
                        autoComplete="off" spellCheck={false}
                        style={{ ...fieldStyle(id), borderBottom: 'none', flex: 1 }}
                      />
                    )}
                  </div>
                </div>
              ))}

              <button type="submit" style={{
                width: '100%', background: '#fff', border: 'none', cursor: 'pointer',
                color: '#060606', fontSize: 9, letterSpacing: '0.34em',
                textTransform: 'uppercase', fontWeight: 700,
                padding: '18px 24px', marginTop: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                transition: 'background 0.2s, box-shadow 0.2s',
                boxShadow: '0 0 32px rgba(255,255,255,0.08)',
              }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
                  e.currentTarget.style.boxShadow = '0 0 48px rgba(255,255,255,0.18)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.boxShadow = '0 0 32px rgba(255,255,255,0.08)';
                }}
              >
                Send Enquiry <ArrowRight size={13}/>
              </button>

              <p style={{
                fontSize: 10, color: 'rgba(255,255,255,0.18)',
                textAlign: 'center', marginTop: 20, lineHeight: 1.7,
              }}>
                Sent via WhatsApp · Response within 2 hours
              </p>
              <a href="/for-clients" style={{
                width: '100%', marginTop: 14, padding: '16px 24px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
                fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700,
                whiteSpace: 'nowrap', transition: 'border-color 0.2s, background 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'transparent'; }}
              >
                Share a full brief <ArrowRight size={13}/>
              </a>
            </form>
          )}
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.0, duration: 1 }}
        style={{
          position: 'absolute', bottom: 40, left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
          zIndex: 2,
        }}
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            width: 1, height: 52,
            background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.35))',
          }}
        />
        <span style={{
          fontSize: 7, letterSpacing: '0.44em', textTransform: 'uppercase',
          fontWeight: 600, color: 'rgba(255,255,255,0.18)',
        }}>Scroll</span>
      </motion.div>
    </section>
  );
}


// ─── About ────────────────────────────────────────────────────────────────────
function About() {
  const ref = useRef(null);
  const mobile = useMobile();
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="about" ref={ref} style={{
      padding: mobile ? '80px 24px' : 'clamp(120px, 12vw, 200px) 88px',
      borderTop: '1px solid rgba(255,255,255,0.05)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute', left: '50%', top: '40%',
        transform: 'translate(-50%, -50%)',
        width: 800, height: 400,
        background: 'radial-gradient(ellipse, rgba(255,255,255,0.025) 0%, transparent 70%)',
        filter: 'blur(60px)', pointerEvents: 'none',
      }}/>

      {/* Label row */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: EASE }}
        style={{ display: 'flex', alignItems: 'center', gap: 28, marginBottom: 72 }}
      >
        <span style={{
          fontSize: 9, letterSpacing: '0.44em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.4)', fontWeight: 700, whiteSpace: 'nowrap',
        }}>The Broker</span>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }}/>
        <span style={{
          fontSize: 9, letterSpacing: '0.34em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.18)', fontWeight: 600,
        }}>Pune, India</span>
      </motion.div>

      {/* Name */}
      <div style={{ overflow: 'hidden', marginBottom: 80 }}>
        <motion.h2
          initial={{ y: '100%', opacity: 0 }}
          animate={isInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 1.0, delay: 0.08, ease: EASE }}
          style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(52px, 7vw, 120px)',
            fontWeight: 700, lineHeight: 0.87, letterSpacing: '-0.025em',
            textShadow: '0 0 100px rgba(255,255,255,0.06)',
          }}
        >
          Srimukh{' '}
          <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.55)' }}>Mamidi</em>
        </motion.h2>
      </div>

      {/* Text block */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? '1fr' : '1fr 2px 1fr',
        gap: mobile ? '32px 0' : '0 72px',
        marginBottom: mobile ? 64 : 96,
      }}>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.85, delay: 0.18, ease: EASE }}
          style={{
            fontSize: 15, color: 'rgba(255,255,255,0.48)',
            lineHeight: 1.95, fontWeight: 300,
          }}
        >
          22. Relocated from Bangalore to Pune to build this independently. Previously Sales Head at Broker in Blue. I left because volume-driven brokerage is structurally broken. An agent who earns on commission has no incentive to ever tell you to walk away.
        </motion.p>

        {!mobile && <div style={{ background: 'rgba(255,255,255,0.06)' }}/>}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.85, delay: 0.26, ease: EASE }}
          style={{ display: 'flex', flexDirection: 'column', gap: 32 }}
        >
          <p style={{
            fontSize: 15, color: 'rgba(255,255,255,0.48)',
            lineHeight: 1.95, fontWeight: 300,
          }}>
            I work with a limited roster, not everyone who walks in. Every property I recommend is one I'd personally invest in. My two questions before any deal: "If I had your money, would I buy here?" and "Don't trust me, trust the facts." If the answer to the first is no, we move on.
          </p>
          <button
            onClick={() => document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              alignSelf: 'flex-start', background: 'none', cursor: 'pointer',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.7)', fontSize: 9, letterSpacing: '0.34em',
              textTransform: 'uppercase', fontWeight: 700, padding: '13px 26px',
              display: 'flex', alignItems: 'center', gap: 10,
              transition: 'border-color 0.2s, color 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.boxShadow = '0 0 28px rgba(255,255,255,0.06)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Start a Conversation <ArrowUpRight size={12}/>
          </button>
        </motion.div>
      </div>

      {/* Pillars grid */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.9, delay: 0.38, ease: EASE }}
        style={{
          display: 'grid',
          gridTemplateColumns: mobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          borderLeft: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {[
          { head: 'You First',           sub: 'No deal gets pushed because it benefits my commission. Your outcome is the only metric.' },
          { head: 'Limited Roster',      sub: 'Not everyone who walks in. A smaller client list means every engagement gets my full attention.' },
          { head: 'No Dev Ties',         sub: 'No referral arrangements. No preferred builders. Advice stays clean.' },
          { head: 'The Suit',            sub: 'Worn every single day, not for aesthetics. A discipline ritual. I show up fully prepared, without exception.' },
        ].map(({ head, sub }) => (
          <div key={head} style={{
            padding: '40px 36px',
            borderRight: '1px solid rgba(255,255,255,0.07)',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            transition: 'background 0.3s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            data-cursor
          >
            <div style={{
              fontSize: 9, letterSpacing: '0.4em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.6)', fontWeight: 700, marginBottom: 16,
            }}>{head}</div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', lineHeight: 1.85, fontWeight: 300 }}>
              {sub}
            </p>
          </div>
        ))}
      </motion.div>
    </section>
  );
}

// ─── Process ──────────────────────────────────────────────────────────────────
function Process() {
  const sectionRef = useRef(null);
  const mobile = useMobile();
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="process" ref={sectionRef} style={{
      display: 'grid',
      gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    }}>
      {/* Left: sticky — desktop only */}
      {!mobile && (
        <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
          <img src={IMAGES.process} alt="" style={{
            width: '100%', height: '100%', objectFit: 'cover',
            filter: 'brightness(0.25) contrast(1.15) saturate(0.4)',
          }}/>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to right, transparent 50%, #060606), linear-gradient(to top, rgba(6,6,6,0.9) 28%, transparent 55%)',
          }}/>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 60% 50% at 30% 60%, rgba(255,255,255,0.04) 0%, transparent 65%)',
          }}/>
          <div style={{
            position: 'absolute', top: '50%', left: '-8%',
            transform: 'translateY(-50%)',
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(140px, 18vw, 260px)',
            fontWeight: 700, color: 'rgba(255,255,255,0.03)',
            lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
            letterSpacing: '-0.04em',
          }}>05</div>
          <div style={{ position: 'absolute', bottom: 72, left: 60 }}>
            <div style={{
              fontSize: 9, letterSpacing: '0.42em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)', fontWeight: 700, marginBottom: 20,
            }}>The Process</div>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(38px, 4vw, 64px)',
              fontWeight: 700, lineHeight: 0.9,
              textShadow: '0 0 80px rgba(255,255,255,0.1)',
            }}>
              From Brief<br/>
              to{' '}
              <em style={{ color: 'rgba(255,255,255,0.65)', fontStyle: 'italic' }}>Keys.</em>
            </h2>
          </div>
        </div>
      )}

      {/* Steps column */}
      <div style={{
        padding: mobile ? '80px 24px 80px 52px' : '128px 80px 128px 72px',
        position: 'relative',
      }}>
        {/* Mobile section header */}
        {mobile && (
          <div style={{ marginBottom: 64 }}>
            <div style={{
              fontSize: 9, letterSpacing: '0.42em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)', fontWeight: 700, marginBottom: 20,
            }}>The Process</div>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(36px, 10vw, 56px)',
              fontWeight: 700, lineHeight: 0.9,
            }}>
              From Brief<br/>
              to{' '}
              <em style={{ color: 'rgba(255,255,255,0.65)', fontStyle: 'italic' }}>Keys.</em>
            </h2>
          </div>
        )}

        <div style={{
          position: 'absolute',
          left: mobile ? 24 : 36,
          top: mobile ? 80 : 148,
          bottom: mobile ? 80 : 148,
          width: 1, background: 'rgba(255,255,255,0.06)', overflow: 'hidden',
        }}>
          <motion.div style={{
            width: '100%', height: '100%',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.6), rgba(255,255,255,0.2))',
            scaleY: lineScaleY, transformOrigin: 'top',
          }}/>
        </div>

        {PROCESS_STEPS.map((step, i) => (
          <ProcessStep key={step.num} step={step} last={i === PROCESS_STEPS.length - 1} mobile={mobile}/>
        ))}
      </div>
    </section>
  );
}

function ProcessStep({ step, last, mobile }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 28 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.9, ease: EASE }}
      style={{ position: 'relative', paddingBottom: last ? 0 : 96 }}
    >
      {/* Diamond */}
      <div style={{
        position: 'absolute', left: mobile ? -36 : -47, top: 8,
        width: 10, height: 10, transform: 'rotate(45deg)',
        background: isInView ? '#fff' : 'transparent',
        border: `1px solid ${isInView ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)'}`,
        boxShadow: isInView ? '0 0 16px rgba(255,255,255,0.3)' : 'none',
        transition: 'background 0.5s, border-color 0.5s, box-shadow 0.5s',
      }}/>

      {/* Ghost step num */}
      <div style={{
        position: 'absolute', right: 0, top: -18,
        fontFamily: 'Playfair Display, serif',
        fontSize: 72, fontWeight: 700, color: 'rgba(255,255,255,0.04)',
        lineHeight: 1, letterSpacing: '-0.02em',
        userSelect: 'none', pointerEvents: 'none',
      }}>{step.num}</div>

      <div style={{
        fontSize: 9, letterSpacing: '0.38em', color: 'rgba(255,255,255,0.35)',
        fontWeight: 700, marginBottom: 14, textTransform: 'uppercase',
      }}>{step.num}</div>
      <h3 style={{
        fontFamily: 'Playfair Display, serif', fontSize: 30,
        fontWeight: 700, lineHeight: 1.05, marginBottom: 18, letterSpacing: '-0.01em',
      }}>{step.title}</h3>
      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', lineHeight: 1.9, maxWidth: 380 }}>
        {step.body}
      </p>
    </motion.div>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const mobile = useMobile();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer style={{
      background: '#020202',
      borderTop: '1px solid rgba(255,255,255,0.05)',
      padding: mobile ? '64px 24px 36px' : '88px 88px 44px',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? '1fr' : '1.6fr 1fr 1fr',
        gap: mobile ? 48 : 64,
        paddingBottom: mobile ? 48 : 80,
        borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: 36,
      }}>
        <div>
          <div style={{ marginBottom: 20 }}>
            <img
              src="/logo-white.svg" alt="BrokerInASuit"
              style={{ height: 22, aspectRatio: '300 / 38.446', display: 'block' }}
            />
            <div style={{
              fontFamily: 'Helvetica Neue, Helvetica, sans-serif', fontSize: 8,
              fontWeight: 600, letterSpacing: '0.28em', color: 'rgba(255,255,255,0.35)',
              textTransform: 'uppercase', lineHeight: 1, marginTop: 5,
            }}>Real Estate Tailored For You</div>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.22)', lineHeight: 1.85, maxWidth: 300 }}>
            Independent real estate advisory. No developer ties. No volume targets. Just you, the facts, and a broker who shows up prepared. Every single day.
          </p>
        </div>

        <div>
          <div style={{
            fontSize: 8, letterSpacing: '0.42em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.4)', fontWeight: 700, marginBottom: 28,
          }}>Navigate</div>
          {['about', 'process'].map(id => (
            <button key={id} onClick={() => go(id)} style={{
              display: 'block', background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.22)', fontSize: 13, fontWeight: 300,
              marginBottom: 14, padding: 0, transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.target.style.color = '#fff'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.22)'}
            >{id[0].toUpperCase() + id.slice(1)}</button>
          ))}
          {[['/for-clients', 'For clients'], ['/for-brokers', 'For brokers']].map(([href, label]) => (
            <a key={href} href={href} style={{
              display: 'block', color: 'rgba(255,255,255,0.22)', fontSize: 13, fontWeight: 300,
              marginBottom: 14, transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.target.style.color = '#fff'}
              onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.22)'}
            >{label}</a>
          ))}
        </div>

        <div>
          <div style={{
            fontSize: 8, letterSpacing: '0.42em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.4)', fontWeight: 700, marginBottom: 28,
          }}>Contact</div>
          {['foryou@brokerinasuit.com', '+91 77700 60901', 'Pune, Maharashtra'].map(t => (
            <div key={t} style={{
              fontSize: 13, color: 'rgba(255,255,255,0.22)', fontWeight: 300, marginBottom: 14,
            }}>{t}</div>
          ))}
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: mobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: mobile ? 'flex-start' : 'center',
        gap: mobile ? 8 : 0,
      }}>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.14)', fontWeight: 300 }}>
          © {new Date().getFullYear()} Broker in a Suit. All rights reserved.
        </span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.14)', fontWeight: 300, letterSpacing: '0.1em' }}>
          Pune · 18.52° N · 73.85° E
        </span>
      </div>
    </footer>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const brandRef = useRef(null);
  // intro → moving (logo gliding, page revealing) → done
  const [stage, setStage] = useState('intro');

  useEffect(() => {
    document.body.style.overflow = stage === 'intro' ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [stage]);

  return (
    <>
      <style>{G}</style>
      <Grain/>
      <Nav brandRef={brandRef} stage={stage}/>
      {stage !== 'done' && (
        <Intro
          targetRef={brandRef}
          onMove={() => setStage('moving')}
          onDone={() => setStage('done')}
        />
      )}
      {stage !== 'intro' && (
        <>
          <main>
            <Hero/>
            <About/>
            <Process/>
          </main>
          <Footer/>
        </>
      )}
    </>
  );
}
